import { NavLink } from "react-router-dom";

import { Wordmark } from "@/components/brand/Wordmark";
import { cn } from "@/lib/cn";

interface SidebarItem {
  to: string;
  label: string;
  /** Single-character glyph rendered as the icon. Keeping it as plain
   *  text avoids a runtime icon library dependency for the cockpit
   *  scaffolding; later phases can swap to lucide-react. */
  glyph: string;
  /** Optional badge rendered to the right of the label (e.g. an
   *  overdue / escalation count). */
  badge?: number | null;
}

interface SidebarProps {
  items: SidebarItem[];
  /** Renders the wordmark + a compact context line under it. The
   *  user's email is the standard context; the cockpit passes it
   *  through so signed-out renders cleanly. */
  contextLine?: string;
  onSignOut?: () => void;
  signingOut?: boolean;
}

/** Reference-design left rail: vertical strip with the Oprentis
 *  wordmark, icon nav, and a sign-out affordance at the bottom. Used
 *  by every cockpit screen so the navigation stays consistent across
 *  views. */
export function Sidebar({
  items,
  contextLine,
  onSignOut,
  signingOut,
}: SidebarProps) {
  return (
    <aside
      data-testid="cockpit-sidebar"
      className="hidden h-screen w-56 shrink-0 flex-col gap-6 border-r border-op-border bg-op-surface px-4 py-5 lg:flex"
    >
      <div>
        <Wordmark className="text-base" />
        {contextLine ? (
          <p className="mt-2 truncate text-[11px] text-op-muted" title={contextLine}>
            {contextLine}
          </p>
        ) : null}
      </div>

      <nav className="flex flex-col gap-1" aria-label="Primary">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end
            className={({ isActive }) =>
              cn(
                "flex items-center justify-between rounded px-3 py-2 text-sm transition-colors",
                isActive
                  ? "bg-op-accent-muted text-op-primary"
                  : "text-op-secondary hover:bg-op-elevated hover:text-op-primary",
              )
            }
            data-testid={`sidebar-link-${item.to.replace(/\//g, "") || "home"}`}
          >
            <span className="flex items-center gap-3">
              <span
                aria-hidden
                className="inline-flex h-5 w-5 items-center justify-center text-base text-op-muted"
              >
                {item.glyph}
              </span>
              {item.label}
            </span>
            {item.badge && item.badge > 0 ? (
              <span
                className="rounded-full bg-bucket-need-reply-now/20 px-1.5 py-0.5 font-mono text-[10px] text-bucket-need-reply-now"
                aria-label={`${item.badge} attention items`}
              >
                {item.badge}
              </span>
            ) : null}
          </NavLink>
        ))}
      </nav>

      <div className="mt-auto">
        {onSignOut ? (
          <button
            type="button"
            onClick={onSignOut}
            disabled={signingOut}
            data-testid="sign-out-button"
            className="w-full rounded border border-op-border bg-op-elevated px-3 py-2 text-xs text-op-secondary transition-colors hover:bg-op-base hover:text-op-primary disabled:opacity-50"
          >
            {signingOut ? "Signing out…" : "Sign out"}
          </button>
        ) : null}
      </div>
    </aside>
  );
}
